import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import DayDetail from './DayDetail';
import YouTubeFrame from './YouTubeFrame';
import { translations } from '../translations';
import apiService from '../services/apiService';

function DayDetailPage({ language = 'en', patientData }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { phaseId, dayNumber } = useParams();
  const [dayData, setDayData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const { day } = location.state || {};

  useEffect(() => {
    const loadDayData = async () => {
      if (!patientData || !patientData.patientId) {
        setError('Données patient manquantes');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Si on a les données dans location.state, les utiliser
        if (day) {
          setDayData(day);
          setLoading(false);
          return;
        }

        // Sinon, recharger depuis le serveur
        if (phaseId && dayNumber) {
          const response = await apiService.getPatientTimeline(patientData.patientId);
          if (response.success && response.timeline) {
            // Trouver la phase et le jour correspondants
            const phase = response.timeline.find(p => p.phaseId === phaseId);
            if (phase) {
              const day = phase.days.find(d => d.dayNumber === parseInt(dayNumber));
              if (day) {
                setDayData(day);
                setLoading(false);
                return;
              }
            }
          }
        }

        // Si on n'a pas trouvé les données, rediriger
        setError('Jour non trouvé');
        setTimeout(() => navigate(-1), 2000);
      } catch (error) {
        console.error('Erreur chargement données jour:', error);
        setError('Erreur lors du chargement des données');
        setTimeout(() => navigate(-1), 2000);
      } finally {
        setLoading(false);
      }
    };

    loadDayData();
  }, [patientData, phaseId, dayNumber, day, navigate]);

  if (loading) {
    return (
      <div className="day-detail-page" style={{ padding: '20px' }}>
        <div className="loading-spinner">Chargement...</div>
      </div>
    );
  }

  if (error || !dayData) {
    return (
      <div className="day-detail-page" style={{ padding: '20px' }}>
        <div className="error-message">{error || 'Jour non trouvé'}</div>
      </div>
    );
  }

  const t = translations[language] || translations.en;

  // Données des vidéos pour la phase FASTENBOX
  const fastingVideos = [
    {
      day: 1,
      title: language === 'de' ? 'Tag 1: Transition' : 'Day 1: Transition',
      url: 'https://youtu.be/13cAFZQnwNM?si=tXDNlKAB33B2BNsg'
    },
    {
      day: 2,
      title: language === 'de' ? 'Tag 2' : 'Day 2',
      url: 'https://youtu.be/dMYYPoDkUi4?si=fDGJ2QWsIMsqzoHe'
    },
    {
      day: 3,
      title: language === 'de' ? 'Tag 3' : 'Day 3',
      url: 'https://youtu.be/xgyPNywykAo?si=PXGlCmPaPiLSDHHt'
    },
    {
      day: 4,
      title: language === 'de' ? 'Tag 4' : 'Day 4',
      url: 'https://youtu.be/CWcorsqaIps?si=WkkR0FLpvW6DU7FE'
    },
    {
      day: 5,
      title: language === 'de' ? 'Tag 5: Restart' : 'Day 5: Restart',
      url: 'https://youtu.be/ff8AQg2Tk7o?si=bZ0eLfrdnDOjwfAS'
    }
  ];

  // Fonction pour mapper les jours de jeûne aux vidéos
  const getFastingVideoIndex = (dayNumber) => {
    // Jours -20 à -16 (5 jours) -> vidéos 1-5
    if (dayNumber >= -20 && dayNumber <= -16) {
      return Math.abs(dayNumber + 20) + 1; // -20 -> 1, -19 -> 2, ..., -16 -> 5
    }
    // Jours -10 à -6 (5 jours) -> vidéos 1-5 (répétition)
    if (dayNumber >= -10 && dayNumber <= -6) {
      return Math.abs(dayNumber + 10) + 1; // -10 -> 1, -9 -> 2, ..., -6 -> 5
    }
    return null;
  };

  // Vérifier si on doit afficher une vidéo
  const shouldShowVideo = (phaseId === 'buchinger' && dayData && dayData.dayNumber >= 1 && dayData.dayNumber <= 5) ||
                         (dayData && (dayData.dayNumber >= -20 && dayData.dayNumber <= -16) || 
                          (dayData.dayNumber >= -10 && dayData.dayNumber <= -6));
  
  let videoData = null;
  if (shouldShowVideo) {
    if (phaseId === 'buchinger' && dayData.dayNumber >= 1 && dayData.dayNumber <= 5) {
      // Jours positifs de Buchinger (logique existante)
      videoData = fastingVideos.find(v => v.day === dayData.dayNumber);
    } else {
      // Jours de jeûne négatifs
      const videoIndex = getFastingVideoIndex(dayData.dayNumber);
      if (videoIndex) {
        videoData = fastingVideos.find(v => v.day === videoIndex);
      }
    }
  }

  return (
    <div className="day-detail-page" style={{ padding: '20px' }}>
      <button onClick={() => navigate(-1)} className="day-back-btn">
        <img src="/images/back.png" alt="Back" className="back-icon" />
        <span className="label">{t.dayDetail?.back || (language === 'de' ? 'Zurück' : language === 'fr' ? 'Retour' : 'Back')}</span>
      </button>
      
      <DayDetail
        day={dayData}
        phaseId={phaseId}
        patientData={patientData}
        language={language}
        onClose={() => navigate(-1)}
        onTaskUpdate={() => {
          // Les données sont déjà mises à jour localement dans DayDetail
          // Pas besoin de recharger toute la page
        }}
        videoData={videoData}
      />
    </div>
  );
}

export default DayDetailPage; 